import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Tooltip} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {PipelineStep} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import RightSidebarPreviewPane from '../previews/RightSidebarPreviewPane';

export enum RightSidebarWidth {
  STANDARD = 275,
  MEDIUM = 375,
  WIDE = 550,
}

export interface RightSidebarConfirmationButton {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  isPrimary?: boolean;
  dataTestAttrsElement: string;
}

interface BaseRightSidebarSkeletonProps {
  title: string;
  children: React.ReactNode;
  confirmationButtons?: Array<RightSidebarConfirmationButton>;
  width: RightSidebarWidth;
  // true for absolute positioning (overlay mode) or false for flex layout (side-by-side mode).
  useAbsolutePositioning?: boolean;
  dataTestAttrs: DataTestAttrs;
}

interface RightSidebarSkeletonPropsWithPreview extends BaseRightSidebarSkeletonProps {
  draftStep: PipelineStep;
  originalStep: PipelineStep;
  clientStateId: string;
}

interface RightSidebarSkeletonPropsWithoutPreview extends BaseRightSidebarSkeletonProps {
  draftStep?: never;
  originalStep?: never;
  clientStateId?: never;
}

export type RightSidebarSkeletonProps =
  | RightSidebarSkeletonPropsWithPreview
  | RightSidebarSkeletonPropsWithoutPreview;

function hasPreviewData(props: RightSidebarSkeletonProps): props is RightSidebarSkeletonPropsWithPreview {
  return (
    props.draftStep !== undefined && props.originalStep !== undefined && props.clientStateId !== undefined
  );
}

/**
 * Basic UI outline for a right sidebar, with title, content, and confirmation buttons.
 */
export default function RightSidebarSkeleton(props: RightSidebarSkeletonProps): JSX.Element {
  const {title, children, confirmationButtons, width, useAbsolutePositioning = false, dataTestAttrs} = props;

  const [showPreviewPane, setShowPreviewPane] = useState(false);

  const sidebarWidth = showPreviewPane ? '100%' : width;

  return (
    <div
      className={css(
        rightSidebarSkeletonStyles.container,
        useAbsolutePositioning && rightSidebarSkeletonStyles.absolutePositioning
      )}
      style={{
        width: sidebarWidth,
        borderLeft: !showPreviewPane ? `1px solid ${Colors.GRAY_LIGHT}` : 'none',
      }}
      {...getTestProps(dataTestAttrs)}
    >
      <>
        <div className={css(rightSidebarSkeletonStyles.header)}>
          {title}
          {hasPreviewData(props) && (
            <Tooltip
              label={
                showPreviewPane
                  ? 'Collapse sidebar to hide inputs and outputs'
                  : 'Expand sidebar to view inputs and outputs'
              }
            >
              <ActionIcon
                variant="subtle"
                aria-label={showPreviewPane ? 'Collapse inputs and outputs' : 'Expand inputs and outputs'}
                onClick={() => {
                  setShowPreviewPane(!showPreviewPane);
                }}
                {...getTestProps({...dataTestAttrs, element: 'togglePreview'})}
              >
                <BenchlingIcon icon={showPreviewPane ? 'collapse-right-full' : 'expand-right-full'} />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      </>
      <div className={css(rightSidebarSkeletonStyles.body)}>
        <div
          className={css(
            showPreviewPane ? rightSidebarSkeletonStyles.halfPanel : rightSidebarSkeletonStyles.fullWidth
          )}
        >
          {children}
        </div>
        {showPreviewPane && hasPreviewData(props) && (
          <div className={css(rightSidebarSkeletonStyles.halfPanel)}>
            <RightSidebarPreviewPane
              draftStep={props.draftStep}
              originalStep={props.originalStep}
              clientStateId={props.clientStateId}
            />
          </div>
        )}
      </div>
      {confirmationButtons && (
        <div className={css(rightSidebarSkeletonStyles.footer)} style={{width: sidebarWidth}}>
          {confirmationButtons.map(({text, disabled, onClick, isPrimary, dataTestAttrsElement}, index) => (
            <Button
              buttonStyle={isPrimary ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_GRAY}
              className={css(rightSidebarSkeletonStyles.confirmationButton)}
              disabled={disabled}
              key={`${title}-${index}`}
              onClick={onClick}
              size={ButtonSize.MEDIUM}
              text={text}
              dataTestAttrs={{...dataTestAttrs, element: dataTestAttrsElement}}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const DEFAULT_PADDING = 10;
export const STEP_CONFIG_PANEL_WIDTH = 500;

export const rightSidebarSkeletonStyles = StyleSheet.create({
  container: {
    background: Colors.WHITE,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'width 0.3s ease-in-out',
    overflowX: 'hidden',
  },
  absolutePositioning: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
  },
  header: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    padding: DEFAULT_PADDING,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: DEFAULT_PADDING,
    minHeight: 0,
  },
  halfPanel: {
    width: '50%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: DEFAULT_PADDING,
  },
  fullWidth: {
    width: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: DEFAULT_PADDING,
  },
  footer: {
    background: Colors.WHITE,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    display: 'flex',
    padding: DEFAULT_PADDING,
    flexShrink: 0,
  },
  confirmationButton: {
    flex: 1,
    margin: DEFAULT_PADDING / 2,
  },
});
